import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import { alpha } from '@mui/material/styles'
import { FLAG_LABEL, MovementRow } from '../types'

type PillStyle = {
	label: string,
	colorKey: 'success' | 'error' | 'warning' | 'grey',
}

function pillStyle( row: MovementRow ): PillStyle {
	switch ( row.direction ) {
		case 'in':
			return { label: `+${row.quantity}`, colorKey: 'success' }
		case 'out':
			return { label: `−${row.quantity}`, colorKey: 'error' }
		case 'unchanged':
			return { label: '±0', colorKey: 'grey' }
		default:
			return { label: `${row.quantity} units`, colorKey: 'warning' }
	}
}

/**
 * The signed quantity delta — green in, red out, muted ±0 for a movement that nets to
 * nothing, warning for an indeterminate direction shown unsigned.
 *
 * A cancelled movement keeps its figure but is struck through and drained of colour: the
 * API reports what *would* have moved (users want to know how much came back), so the
 * number has to stay readable while never reading as an applied change. An in-transit
 * movement keeps its normal colour, because those units really did leave this location —
 * `StatusChip` carries the "not yet received" caveat.
 */
export default function DeltaPill( { row }: { row: MovementRow } ) {
	const { label, colorKey } = pillStyle( row )
	const isCancelled = row.status === 'cancelled'
	const isGrey = colorKey === 'grey' || isCancelled
	const isUnknown = row.direction === 'unknown'

	const pill = (
		<Box
			component="span"
			sx={{
				display: 'inline-block',
				px: 1,
				py: 0.25,
				borderRadius: 3,
				fontSize: 13,
				fontWeight: 600,
				fontVariantNumeric: 'tabular-nums',
				whiteSpace: 'nowrap',
				cursor: isUnknown || isCancelled ? 'help' : undefined,
				textDecoration: isCancelled ? 'line-through' : undefined,
				color: isCancelled ? 'text.disabled' : isGrey ? 'text.secondary' : `${colorKey}.dark`,
				bgcolor: ( theme ) => ( isGrey ? theme.palette.grey[ 200 ] : alpha( theme.palette[ colorKey ].main, 0.16 ) ),
			}}
		>
			{label}
		</Box>
	)

	if ( isCancelled ) {
		return <Tooltip title={FLAG_LABEL.cancelled} arrow>{pill}</Tooltip>
	}

	// Without the expander from the comp, the warning pill is the only cue that the sign is
	// unknown, so it carries its own explanation.
	return isUnknown
		? <Tooltip title={FLAG_LABEL.indeterminate_direction} arrow>{pill}</Tooltip>
		: pill
}
